import { supabase } from '../supabase';
import type { Customer, CustomerTag } from '../customers';
import { getCustomerTags } from '../customers';

export type SegmentSystemKey = 'all_contacts' | 'customers' | 'active_customers';

export interface SegmentRule {
  id?: string;
  position: number;
  kind: string;
  operator: string;
  value: Record<string, unknown>;
}

export interface CustomerSegment {
  id: string;
  business_id: string;
  name: string;
  system_key: SegmentSystemKey | null;
  is_lead_list: boolean;
  visible: boolean;
  rules: SegmentRule[];
}

export const RULE_KINDS: { id: string; label: string; operator: string; hint: string }[] = [
  { id: 'tags', label: 'Tag', operator: 'includes', hint: 'Has one of these tags' },
  { id: 'address_contains', label: 'Address contains', operator: 'is', hint: 'City or street words' },
  { id: 'source_is', label: 'Source is', operator: 'is', hint: 'Where they came from' },
  { id: 'contact', label: 'Email or phone', operator: 'has_email', hint: 'Has or is missing contact info' },
  { id: 'customer_type', label: 'Person or business', operator: 'is', hint: '' },
  { id: 'is_supplier', label: 'Supplier', operator: 'is', hint: '' },
  { id: 'created_within_days', label: 'Added in the last', operator: 'is', hint: 'Days' },
  { id: 'booking', label: 'Booking', operator: 'has', hint: 'Has or does not have a booking' },
  { id: 'invoice_within_days', label: 'Invoice in the last', operator: 'has', hint: 'Days. This is the Active customers rule.' },
  { id: 'ever_invoice', label: 'Ever invoiced', operator: 'has', hint: '' },
  { id: 'ever_booking', label: 'Ever booked', operator: 'has', hint: '' },
  { id: 'amount_spent', label: 'Amount spent', operator: 'at_least', hint: '' },
];

export function emptyRule(kind = 'tags'): SegmentRule {
  if (kind === 'tags') return { position: 0, kind, operator: 'includes', value: { tagIds: [] } };
  if (kind === 'contact') return { position: 0, kind, operator: 'has_email', value: {} };
  if (kind === 'customer_type') return { position: 0, kind, operator: 'is', value: { type: 'person' } };
  if (kind === 'is_supplier') return { position: 0, kind, operator: 'is', value: {} };
  if (kind === 'created_within_days') return { position: 0, kind, operator: 'is', value: { days: 30 } };
  if (kind === 'booking') return { position: 0, kind, operator: 'has', value: { days: 90 } };
  if (kind === 'invoice_within_days') return { position: 0, kind, operator: 'has', value: { days: 45 } };
  if (kind === 'ever_invoice' || kind === 'ever_booking') return { position: 0, kind, operator: 'has', value: {} };
  if (kind === 'amount_spent') return { position: 0, kind, operator: 'at_least', value: { amount: 0 } };
  if (kind === 'address_contains' || kind === 'source_is') return { position: 0, kind, operator: 'is', value: { text: '' } };
  return { position: 0, kind, operator: 'is', value: {} };
}

export async function ensureCustomerSegments(businessId: string): Promise<void> {
  const { error } = await supabase.rpc('ensure_customer_segments', { p_business_id: businessId });
  if (error) throw error;
}

export async function listCustomerSegments(businessId: string): Promise<CustomerSegment[]> {
  try {
    await ensureCustomerSegments(businessId);
  } catch (error) {
    console.error('Could not prepare customer segments:', error);
  }
  const { data, error } = await supabase
    .from('customer_segments')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const segments = (data || []) as CustomerSegment[];
  if (segments.length === 0) return [];
  const { data: rules, error: rulesError } = await supabase
    .from('customer_segment_rules')
    .select('*')
    .in('segment_id', segments.map((s) => s.id))
    .order('position', { ascending: true });
  if (rulesError) throw rulesError;
  const bySegment = new Map<string, SegmentRule[]>();
  for (const rule of (rules || []) as (SegmentRule & { segment_id: string })[]) {
    const list = bySegment.get(rule.segment_id) || [];
    list.push({
      id: rule.id,
      position: rule.position,
      kind: rule.kind,
      operator: rule.operator,
      value: (rule.value || {}) as Record<string, unknown>,
    });
    bySegment.set(rule.segment_id, list);
  }
  const order = (s: CustomerSegment) => {
    if (s.system_key === 'customers') return 0;
    if (s.system_key === 'all_contacts') return 1;
    if (s.system_key === 'active_customers') return 2;
    return 3;
  };
  return segments
    .map((s) => ({ ...s, rules: bySegment.get(s.id) || [] }))
    .sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
}

/** Every customer id in one segment. The list call caps each page at 5000. */
export async function listAllSegmentCustomerIds(businessId: string, segmentId: string): Promise<string[]> {
  const ids: string[] = [];
  const pageSize = 5000;
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  while (offset < total) {
    const page = await listSegmentCustomerIds(businessId, segmentId, { limit: pageSize, offset });
    total = page.total;
    ids.push(...page.ids);
    if (page.ids.length === 0) break;
    offset += page.ids.length;
  }
  return ids;
}

/** Union of customers across several segments. */
export async function unionSegmentCustomerIds(businessId: string, segmentIds: string[]): Promise<Set<string>> {
  const unique = [...new Set(segmentIds.filter(Boolean))];
  const pages = await Promise.all(unique.map((id) => listAllSegmentCustomerIds(businessId, id)));
  return new Set(pages.flat());
}

export async function listSegmentCustomerIds(
  businessId: string,
  segmentId: string,
  opts: { search?: string; limit?: number; offset?: number } = {},
): Promise<{ total: number; ids: string[] }> {
  const { data, error } = await supabase.rpc('list_segment_customer_ids', {
    p_business_id: businessId,
    p_segment_id: segmentId,
    p_search: opts.search || '',
    p_limit: opts.limit ?? 30,
    p_offset: opts.offset ?? 0,
  });
  if (error) throw error;
  const payload = (data || {}) as { total?: number; ids?: string[] };
  return { total: Number(payload.total) || 0, ids: Array.isArray(payload.ids) ? payload.ids : [] };
}

export async function loadSegmentCustomers(
  businessId: string,
  segmentId: string,
  opts: { search?: string; page?: number; pageSize?: number } = {},
): Promise<{ rows: Customer[]; total: number }> {
  const pageSize = opts.pageSize ?? 30;
  const page = Math.max(opts.page ?? 1, 1);
  const { total, ids } = await listSegmentCustomerIds(businessId, segmentId, {
    search: opts.search,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  if (ids.length === 0) return { rows: [], total };
  const { data, error } = await supabase
    .from('customers')
    .select('*, vehicles:customer_vehicles(*)')
    .in('id', ids);
  if (error) throw error;
  const byId = new Map(((data || []) as Customer[]).map((c) => [c.id, c]));
  return { rows: ids.map((id) => byId.get(id)).filter((c): c is Customer => Boolean(c)), total };
}

export async function customersInSegment(
  businessId: string,
  segmentId: string,
  customerIds: string[],
): Promise<Set<string>> {
  if (customerIds.length === 0) return new Set();
  const { data, error } = await supabase.rpc('customers_in_segment', {
    p_business_id: businessId,
    p_segment_id: segmentId,
    p_customer_ids: customerIds,
  });
  if (error) throw error;
  return new Set((data || []) as string[]);
}

export async function saveCustomerSegment(
  businessId: string,
  input: {
    id?: string;
    name: string;
    isLeadList: boolean;
    visible?: boolean;
    rules: SegmentRule[];
    systemKey?: SegmentSystemKey | null;
  },
): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new Error('A segment needs a name.');
  if (!input.systemKey && input.rules.length === 0) throw new Error('Add at least one condition.');

  let id = input.id;
  if (!id) {
    const { data, error } = await supabase
      .from('customer_segments')
      .insert({
        business_id: businessId,
        name,
        is_lead_list: input.isLeadList,
        visible: input.visible !== false,
      })
      .select('id')
      .single();
    if (error) throw error;
    id = data.id as string;
  } else {
    const patch: Record<string, unknown> = {
      name,
      is_lead_list: input.isLeadList,
    };
    if (input.visible !== undefined) patch.visible = input.visible;
    const { error } = await supabase.from('customer_segments').update(patch).eq('id', id).eq('business_id', businessId);
    if (error) throw error;
    const { error: wipe } = await supabase.from('customer_segment_rules').delete().eq('segment_id', id);
    if (wipe) throw wipe;
  }

  if (input.rules.length > 0) {
    const { error } = await supabase.from('customer_segment_rules').insert(
      input.rules.map((rule, index) => ({
        segment_id: id,
        position: index,
        kind: rule.kind,
        operator: rule.operator,
        value: rule.value,
      })),
    );
    if (error) throw error;
  }
  return id;
}

export async function deleteCustomerSegment(businessId: string, segmentId: string): Promise<void> {
  const { error } = await supabase
    .from('customer_segments')
    .delete()
    .eq('id', segmentId)
    .eq('business_id', businessId)
    .is('system_key', null);
  if (error) throw error;
}

export function segmentCountLabel(segment: CustomerSegment, total: number): string {
  if (segment.system_key === 'active_customers') return total === 1 ? 'active customer' : 'active customers';
  if (segment.system_key === 'all_contacts') return total === 1 ? 'contact' : 'contacts';
  if (segment.system_key === 'customers') return total === 1 ? 'customer' : 'customers';
  return total === 1 ? 'person' : 'people';
}

export async function tagsForBusiness(businessId: string): Promise<CustomerTag[]> {
  return getCustomerTags(businessId);
}
